import React, { useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
} from "@mui/material";
import { updateMetaTags } from "../utils/seo";
import Footer from "../components/Footer";

import CodeIcon from "@mui/icons-material/Code";
import WebIcon from "@mui/icons-material/Web";
import StorageIcon from "@mui/icons-material/Storage";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";

interface TechStack {
  language: string;
  description: string;
  color: string;
  frontend: string[];
  backend: string[];
  mobile: string[];
  database: string[];
  popular: boolean;
}

const techStacks: TechStack[] = [
  {
    language: "JavaScript / TypeScript",
    description:
      "Full stack JavaScript development - MERN, MEAN, Next.js, Node.js ecosystem",
    color: "#f7df1e",
    frontend: [
      "React",
      "Next.js",
      "Vue.js",
      "Angular",
      "Svelte",
      "Nuxt.js",
      "Remix",
      "Astro",
    ],
    backend: [
      "Node.js",
      "Express.js",
      "NestJS",
      "Fastify",
      "Koa",
      "Deno",
      "Bun",
    ],
    mobile: ["React Native", "Expo", "Ionic", "NativeScript", "Capacitor"],
    database: [
      "MongoDB",
      "PostgreSQL",
      "MySQL",
      "Redis",
      "Firebase",
      "Supabase",
    ],
    popular: true,
  },
  {
    language: "Python",
    description:
      "Python full stack development - Django, Flask, FastAPI for web and API development",
    color: "#3776ab",
    frontend: [
      "Django Templates",
      "Jinja2",
      "React (REST API)",
      "Vue (REST API)",
    ],
    backend: ["Django", "Flask", "FastAPI", "Pyramid", "Tornado", "Sanic"],
    mobile: ["Kivy", "BeeWare", "React Native (REST API)"],
    database: [
      "PostgreSQL",
      "MySQL",
      "SQLite",
      "MongoDB",
      "Redis",
      "Elasticsearch",
    ],
    popular: true,
  },
  {
    language: "Java",
    description:
      "Enterprise Java full stack - Spring Boot, Java EE, microservices architecture",
    color: "#007396",
    frontend: ["Angular", "React", "Vue.js", "Thymeleaf", "JSF", "Vaadin"],
    backend: [
      "Spring Boot",
      "Spring Framework",
      "Quarkus",
      "Micronaut",
      "Java EE",
      "Dropwizard",
    ],
    mobile: ["Android Native", "Kotlin Multiplatform"],
    database: [
      "PostgreSQL",
      "MySQL",
      "Oracle",
      "MongoDB",
      "Cassandra",
      "Redis",
    ],
    popular: true,
  },
  {
    language: "C# / .NET",
    description:
      "Microsoft .NET stack - ASP.NET Core, Blazor, cross-platform development",
    color: "#239120",
    frontend: ["Blazor", "ASP.NET MVC", "React", "Angular", "Vue.js"],
    backend: [
      "ASP.NET Core",
      "ASP.NET Web API",
      ".NET Core",
      "SignalR",
      "gRPC",
    ],
    mobile: [".NET MAUI", "Xamarin", "Uno Platform"],
    database: [
      "SQL Server",
      "PostgreSQL",
      "MySQL",
      "MongoDB",
      "Azure Cosmos DB",
      "Redis",
    ],
    popular: true,
  },
  {
    language: "Go (Golang)",
    description:
      "Modern Go development - high-performance microservices, cloud-native applications",
    color: "#00add8",
    frontend: ["React (REST API)", "Vue (REST API)", "HTMX", "Templ"],
    backend: ["Gin", "Echo", "Fiber", "Chi", "Gorilla Mux", "Buffalo"],
    mobile: ["Fyne", "React Native (API)"],
    database: [
      "PostgreSQL",
      "MySQL",
      "MongoDB",
      "Redis",
      "CockroachDB",
      "TimescaleDB",
    ],
    popular: true,
  },
  {
    language: "PHP",
    description:
      "PHP web development - Laravel, Symfony, WordPress, modern PHP frameworks",
    color: "#777bb4",
    frontend: [
      "Laravel Blade",
      "Twig",
      "React",
      "Vue.js",
      "Alpine.js",
      "Livewire",
    ],
    backend: ["Laravel", "Symfony", "CodeIgniter", "Slim", "Yii", "CakePHP"],
    mobile: ["React Native (API)", "Flutter (API)"],
    database: ["MySQL", "PostgreSQL", "MariaDB", "MongoDB", "Redis", "SQLite"],
    popular: true,
  },
  {
    language: "Ruby",
    description:
      "Ruby on Rails full stack - rapid development, MVC architecture, RESTful APIs",
    color: "#cc342d",
    frontend: ["Rails Views (ERB)", "Hotwire", "Stimulus", "React", "Vue.js"],
    backend: ["Ruby on Rails", "Sinatra", "Hanami", "Grape API"],
    mobile: ["React Native (API)", "Flutter (API)"],
    database: ["PostgreSQL", "MySQL", "SQLite", "MongoDB", "Redis"],
    popular: false,
  },
  {
    language: "Rust",
    description:
      "High-performance Rust development - memory-safe systems programming, WebAssembly",
    color: "#ce422b",
    frontend: ["Yew", "Leptos", "Dioxus", "React (WASM)", "WebAssembly"],
    backend: ["Actix Web", "Rocket", "Axum", "Warp", "Tide"],
    mobile: ["Tauri"],
    database: [
      "PostgreSQL",
      "MySQL",
      "SQLite",
      "Redis",
      "MongoDB",
      "SurrealDB",
    ],
    popular: false,
  },
  {
    language: "Kotlin",
    description:
      "Modern Kotlin development - Android, Spring Boot, multiplatform mobile",
    color: "#7f52ff",
    frontend: ["React", "Vue.js", "Angular", "Kotlin/JS"],
    backend: ["Spring Boot (Kotlin)", "Ktor", "Micronaut", "Javalin", "Vert.x"],
    mobile: [
      "Android Native",
      "Kotlin Multiplatform",
      "Jetpack Compose",
      "Compose Multiplatform",
    ],
    database: ["PostgreSQL", "MySQL", "MongoDB", "Redis", "Firebase"],
    popular: false,
  },
  {
    language: "Swift",
    description:
      "Apple ecosystem development - iOS, macOS, server-side Swift development",
    color: "#fa7343",
    frontend: ["SwiftUI", "React (API)", "Vue (API)"],
    backend: ["Vapor", "Kitura", "Perfect"],
    mobile: ["iOS Native", "SwiftUI", "UIKit"],
    database: ["PostgreSQL", "MySQL", "MongoDB", "Redis", "SQLite"],
    popular: false,
  },
  {
    language: "Dart / Flutter",
    description:
      "Cross-platform Dart development - Flutter for mobile, web, and desktop. Ideal for startups: single codebase for iOS, Android, and web apps with fast development",
    color: "#0175c2",
    frontend: ["Flutter Web", "AngularDart"],
    backend: [
      "Shelf",
      "Aqueduct",
      "Dart Frog",
      "Alfred",
      "Serverpod",
      "Angel3",
    ],
    mobile: ["Flutter", "Flutter Native", "Flutter Riverpod", "Flutter BLoC"],
    database: [
      "Firebase",
      "Supabase",
      "PostgreSQL",
      "MySQL",
      "MongoDB",
      "SQLite",
      "Hive",
      "Isar",
    ],
    popular: true,
  },
];

const FullStackGuide: React.FC = () => {
  useEffect(() => {
    updateMetaTags({
      title:
        "Full Stack Development Guide 2025 - JavaScript, Python, Java, Dart Flutter Tech Stacks",
      description:
        "Complete guide to full stack development frameworks and tech stacks. Learn MERN, MEAN, Django, Spring Boot, Flutter, .NET, Laravel, and more. Best frontend, backend, and mobile frameworks for each programming language.",
      keywords:
        "full stack development, MERN stack, MEAN stack, full stack developer, JavaScript full stack, Python Django, Spring Boot, ASP.NET Core, Laravel PHP, Ruby on Rails, Go Gin, Dart Flutter, Flutter cross-platform, React, Node.js, TypeScript, web development frameworks, backend frameworks, frontend frameworks, mobile app development, tech stack 2025, programming languages, software development, Flutter web, Flutter mobile",
      canonical: `${window.location.origin}/tech-stacks`,
      ogTitle:
        "Full Stack Development Guide - Complete Tech Stack Reference 2025",
      ogDescription:
        "Explore modern full stack development options: MERN, Django, Spring Boot, Flutter, .NET, Laravel and more. Find the perfect tech stack for your project.",
      ogUrl: `${window.location.origin}/tech-stacks`,
      ogType: "website",
      twitterCard: "summary_large_image",
      twitterTitle: "Full Stack Development Tech Stacks Guide 2025",
      twitterDescription:
        "Complete guide to choosing the right full stack framework: JavaScript, Python, Java, Dart Flutter, Go, PHP, Ruby, and more.",
    });
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          py: { xs: 6, md: 8 },
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 2,
              textAlign: "center",
              color: "white",
            }}
          >
            🚀 Full Stack Development Guide
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: "rgba(255,255,255,0.9)",
              textAlign: "center",
              mb: 2,
            }}
          >
            Choose Your Tech Stack by Programming Language
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: "rgba(255,255,255,0.8)",
              textAlign: "center",
              maxWidth: "800px",
              mx: "auto",
            }}
          >
            Comprehensive guide to modern full stack development frameworks,
            libraries, and tools for web and mobile applications in 2025
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 6, flex: 1 }}>
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" gutterBottom>
            Popular Tech Stacks for Full Stack Development
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Choose your programming language and discover the complete ecosystem
            of frontend frameworks, backend tools, mobile development options,
            and databases available for building modern web applications.
          </Typography>
          <Typography
            variant="body2"
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "rgba(145, 189, 58, 0.08)",
              borderLeft: "4px solid",
              borderColor: "primary.main",
              borderRadius: 1,
              color: "text.secondary",
            }}
          >
            <strong>Note:</strong> These are popular options to consider when
            building your technology stack. Choose wisely based on your career
            goals, project requirements, and the job market in your region.
            Focus on mastering one stack deeply rather than learning everything
            superficially.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            },
            gap: 4,
          }}
        >
          {techStacks.flatMap((stack, index) => {
            const items = [
              <Box key={stack.language}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: 6,
                    },
                    border: stack.popular ? "2px solid" : "1px solid",
                    borderColor: stack.popular ? stack.color : "divider",
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      <CodeIcon sx={{ fontSize: 40, color: stack.color }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="h5" component="h3" gutterBottom>
                          {stack.language}
                        </Typography>
                      </Box>
                    </Box>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      {stack.description}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <WebIcon sx={{ fontSize: 20, color: stack.color }} />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Frontend
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {stack.frontend.map((tech, i) => (
                          <Chip
                            key={i}
                            label={tech}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>

                    <Box sx={{ mb: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <StorageIcon
                          sx={{ fontSize: 20, color: stack.color }}
                        />
                        <Typography variant="subtitle2" fontWeight={600}>
                          Backend
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {stack.backend.map((tech, i) => (
                          <Chip
                            key={i}
                            label={tech}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    </Box>

                    {stack.mobile.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <PhoneAndroidIcon
                            sx={{ fontSize: 20, color: stack.color }}
                          />
                          <Typography variant="subtitle2" fontWeight={600}>
                            Mobile
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {stack.mobile.map((tech, i) => (
                            <Chip
                              key={i}
                              label={tech}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600 }}
                      >
                        Popular Databases:
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {" "}
                        {stack.database.join(", ")}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Box>,
            ];

            return items;
          })}
        </Box>

        <Box sx={{ mt: 8, p: 4, bgcolor: "background.paper", borderRadius: 2 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            How to Choose Your Full Stack Technology
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>For Beginners:</strong> Start with JavaScript/TypeScript -
            it's versatile, has the largest community, and allows you to build
            both frontend and backend with a single language (MERN or MEAN
            stack).
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>For Enterprise Applications:</strong> Consider Java with
            Spring Boot or C# with .NET Core - they offer robust frameworks,
            excellent tooling, and strong typing.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>For Rapid Development:</strong> Python with Django or Ruby
            on Rails provide "batteries-included" frameworks that accelerate
            development.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>For High Performance:</strong> Go or Rust offer excellent
            performance and are ideal for microservices and cloud-native
            applications.
          </Typography>
          <Typography variant="body1">
            <strong>For Startups:</strong> JavaScript/TypeScript (Next.js +
            Node.js), Python (FastAPI + React), or Flutter/Dart for
            cross-platform mobile apps. Flutter is especially cost-effective for
            startups - single codebase deploys to iOS, Android, and web,
            reducing development time and costs significantly.
          </Typography>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

export default FullStackGuide;
